import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Loyalty as LoyaltyIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';

// Import Google Maps components
import DeliveryLocationSelector from '../components/DeliveryLocationSelector';
import ErrorBoundary from '../components/ErrorBoundary';

function CustomerProfile() {
  const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Form state
  const [profileData, setProfileData] = useState({
    name: '',
    mobile: '',
  });

  // Address management state
  const [addresses, setAddresses] = useState([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    type: 'Home',
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: null,
    longitude: null,
    place_id: null,
  });
  const [addressLoading, setAddressLoading] = useState(false);

  const customerInitials = useMemo(() => {
    if (!customer?.name) return 'CU';
    return customer.name
      .split(' ')
      .map((namePart) => namePart[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [customer?.name]);

  const pointsToNextTier = useMemo(
    () => Math.max(0, 500 - (customer?.points_balance || 0)),
    [customer?.points_balance]
  );

  const hasAddresses = addresses.length > 0;

  const getCustomerToken = useCallback(() => localStorage.getItem('customerToken'), []);

  const loadCustomerProfile = useCallback(async () => {
    try {
      const token = getCustomerToken();
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${backendApiUrl}/customer/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Customer profile loaded data:', result.customer);
          setCustomer(result.customer);
          setAddresses(result.customer.addresses || []);
          setProfileData({
            name: result.customer.name || '',
            mobile: result.customer.mobile || '',
          });
        } else {
          setError(result.message || 'Failed to load profile');
        }
      } else {
        setError('Failed to load profile');
      }
    } catch (error) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [backendApiUrl, getCustomerToken]);

  useEffect(() => {
    loadCustomerProfile();
  }, [loadCustomerProfile]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    // Reset form data to current customer data
    setProfileData({
      name: customer?.name || '',
      mobile: customer?.mobile || '',
    });
    setError(null);
    setSuccess(null);
  };

  const handleSaveProfile = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getCustomerToken();
      const response = await fetch(`${backendApiUrl}/customer/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
           name: profileData.name,
           mobile: profileData.mobile,
          }),
      });

      const result = await response.json();

      if (result.success) {
        setCustomer(result.customer);
        setSuccess('Profile updated successfully');
        setIsEditingProfile(false);
        localStorage.setItem('customerData', JSON.stringify(result.customer));
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (error) {
      setError('Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  // Address management functions
  const handleAddAddress = () => {
    setEditingAddress(null);
    setAddressFormData({
      type: 'Home',
      address: '',
      city: '',
      state: '',
      pincode: '',
      latitude: null,
      longitude: null,
      place_id: null,
    });
    setAddressDialogOpen(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressFormData({
      type: address.type || 'Home',
      address: address.address || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      latitude: address.latitude || null,
      longitude: address.longitude || null,
      place_id: address.place_id || null,
    });
    setAddressDialogOpen(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    setAddressLoading(true);
    try {
      const token = getCustomerToken();
      const response = await fetch(`${backendApiUrl}/customer/addresses/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.success) {
        setAddresses(prev => prev.filter(addr => addr.id !== addressId));
        setSuccess('Address deleted successfully');
      } else {
        setError(result.message || 'Failed to delete address');
      }
    } catch (error) {
      setError('Failed to delete address');
    } finally {
      setAddressLoading(false);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    setAddressLoading(true);
    try {
      const token = getCustomerToken();
      const response = await fetch(`${backendApiUrl}/customer/addresses/${addressId}/default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.success) {
        setAddresses(prev => prev.map(addr => ({
          ...addr,
          is_default: addr.id === addressId
        })));
        setSuccess('Default address updated successfully');
      } else {
        setError(result.message || 'Failed to set default address');
      }
    } catch (error) {
      setError('Failed to set default address');
    } finally {
      setAddressLoading(false);
    }
  };

  const validateAddress = () => {
    if (!addressFormData.address.trim()) {
      setError('Address is required');
      return false;
    }

    if (!addressFormData.city.trim()) {
      setError('City is required');
      return false;
    }

    if (!addressFormData.state.trim()) {
      setError('State is required');
      return false;
    }

    if (!addressFormData.pincode.trim()) {
      setError('Pincode is required');
      return false;
    }

    // Validate pincode format (Indian pincode: 6 digits)
    const pincodeRegex = /^[1-9][0-9]{5}$/;
    if (!pincodeRegex.test(addressFormData.pincode)) {
      setError('Please enter a valid 6-digit pincode');
      return false;
    }

    // Validate coordinates if provided
    if (addressFormData.latitude && addressFormData.longitude) {
      const lat = parseFloat(addressFormData.latitude);
      const lng = parseFloat(addressFormData.longitude);

      if (lat < -90 || lat > 90) {
        setError('Invalid latitude coordinates');
        return false;
      }

      if (lng < -180 || lng > 180) {
        setError('Invalid longitude coordinates');
        return false;
      }
    }

    return true;
  };

  const handleSaveAddress = async () => {
    // Clear any previous errors
    setError(null);

    // Validate address data
    if (!validateAddress()) {
      return;
    }

    setAddressLoading(true);
    try {
      const token = getCustomerToken();
      const url = editingAddress
        ? `${backendApiUrl}/customer/addresses/${editingAddress.id}`
        : `${backendApiUrl}/customer/addresses`;

      const response = await fetch(url, {
        method: editingAddress ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(addressFormData),
      });

      const result = await response.json();
      if (result.success) {
        if (editingAddress) {
          setAddresses(prev => prev.map(addr =>
            addr.id === editingAddress.id ? result.address : addr
          ));
          setSuccess('Address updated successfully');
        } else {
          setAddresses(prev => [result.address, ...prev]);
          setSuccess('Address added successfully');
        }
        setAddressDialogOpen(false);
      } else {
        setError(result.message || 'Failed to save address');
      }
    } catch (error) {
      setError('Failed to save address');
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddressFormChange = (field, value) => {
    setAddressFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !customer) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Alert severity="warning">Customer data not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
        My Profile
      </Typography>

      {/* Profile Header Card */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mr: 3,
                bgcolor: 'primary.main',
                fontSize: '2.5rem',
                fontWeight: 700
              }}
            >
              {customerInitials}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {customer.name}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                Customer Account
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={<LoyaltyIcon />}
                  label={`${customer.points_balance || 0} Points`}
                  color="primary"
                  variant="filled"
                />
                <Chip
                  label={`Tier: ${customer.points_tier || 'Bronze'}`}
                  variant="outlined"
                  color="secondary"
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Profile Information */}
      <Grid container spacing={4}>
        {/* Account Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%' }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  Account Information
                </Typography>
                {!isEditingProfile && (
                  <IconButton
                    onClick={handleEditProfile}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              {isEditingProfile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                  <TextField
                    label="Full Name"
                    value={profileData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />

                  <TextField
                    label="Mobile Number"
                    value={profileData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />

                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={updating}
                      size="small"
                      fullWidth
                    >
                      {updating ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancelEdit}
                      size="small"
                      fullWidth
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Email Address
                      </Typography>
                      <Typography variant="body1">
                        {customer.email}
                      </Typography>
                    </Box>
                  </Box>

                  {customer.mobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Mobile Number
                        </Typography>
                        <Typography variant="body1">
                          {customer.mobile}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Full Name
                      </Typography>
                      <Typography variant="body1">
                        {customer.name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery Addresses */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1 }} />
                Delivery Addresses
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage your delivery addresses. You can add multiple addresses and set one as default for faster checkout.
              </Typography>

              {/* Saved Addresses */}
              {hasAddresses && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Your Addresses
                  </Typography>
                  <Grid container spacing={4}>
                    {addresses.map((address) => (
                      <Grid item xs={12} md={6} key={address.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            border: address.is_default ? '2px solid' : '1px solid',
                            borderColor: address.is_default ? 'primary.main' : 'divider',
                            position: 'relative',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: 3,
                              transform: 'translateY(-2px)'
                            },
                            minHeight: 200
                          }}
                        >
                          <CardContent sx={{ p: 4 }}>
                            {address.is_default && (
                              <Chip
                                label="Default Address"
                                size="small"
                                color="primary"
                                sx={{ position: 'absolute', top: 16, right: 16 }}
                              />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
                                {address.type || 'Home'}
                              </Typography>
                              {address.is_default && (
                                <StarIcon sx={{ color: 'primary.main' }} />
                              )}
                            </Box>
                            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                              {address.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              {address.city}, {address.state} {address.pincode}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                              <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleEditAddress(address)}
                                sx={{ minWidth: 100 }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteAddress(address.id)}
                                disabled={addressLoading}
                                sx={{ minWidth: 100 }}
                              >
                                Delete
                              </Button>
                              {!address.is_default && (
                                <Button
                                  variant="contained"
                                  startIcon={<StarIcon />}
                                  onClick={() => handleSetDefaultAddress(address.id)}
                                  disabled={addressLoading}
                                  sx={{ minWidth: 120 }}
                                >
                                  Set Default
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Add New Address */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {!hasAddresses ? 'Add Your First Address' : 'Add Another Address'}
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddAddress}
                  sx={{ borderRadius: 2 }}
                >
                  + Add New Address
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Loyalty Information */}
      <Grid item xs={12} md={4}>
        <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center' }}>
            <LoyaltyIcon sx={{ mr: 1 }} />
            Loyalty Program
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                  {customer.points_balance || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                  Current Points
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.contrastText' }}>
                  {customer.points_tier || 'Bronze'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.contrastText', opacity: 0.9 }}>
                  Current Tier
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.contrastText' }}>
                  {pointsToNextTier}
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.contrastText', opacity: 0.9 }}>
                  Points to Next Tier
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => window.location.href = '/points'}
              sx={{ px: 4, py: 1.5 }}
            >
              View Points History
            </Button>
          </Box>
        </CardContent>
      </Card>
      </Grid>

      {/* Address Dialog */}
      <Dialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAddress ? 'Edit Address' : 'Add New Address'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Address Type</InputLabel>
              <Select
                value={addressFormData.type}
                label="Address Type"
                onChange={(e) => handleAddressFormChange('type', e.target.value)}
              >
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Work">Work</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <ErrorBoundary fallbackMessage="Advanced location selector is temporarily unavailable. Please refresh the page and try again.">
              <DeliveryLocationSelector
                onLocationConfirm={(locationData) => {
                  // Update form data with selected location
                  handleAddressFormChange('address', locationData.manualAddress || locationData.address);
                  handleAddressFormChange('latitude', locationData.location.lat);
                  handleAddressFormChange('longitude', locationData.location.lng);
                  handleAddressFormChange('city', locationData.city);
                  handleAddressFormChange('state', locationData.state);
                  handleAddressFormChange('pincode', locationData.pincode);

                  // Store place ID for future reference
                  handleAddressFormChange('place_id', locationData.placeId);
                }}
                initialAddress={addressFormData.address}
                initialLocation={
                  addressFormData.latitude && addressFormData.longitude
                    ? { lat: addressFormData.latitude, lng: addressFormData.longitude }
                    : null
                }
                initialCity={addressFormData.city}
                initialState={addressFormData.state}
                initialPincode={addressFormData.pincode}
                initialManualAddress={addressFormData.address}
                label="Full Delivery Address"
                placeholder="Start typing your delivery address for suggestions..."
                height={400}
              />
            </ErrorBoundary>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            disabled={addressLoading}
          >
            {addressLoading ? <CircularProgress size={20} /> : (editingAddress ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerProfile;
